import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center border px-2.5 py-0.5 text-xs font-semibold transition-colors duration-brand-fast ease-brand focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent border-2 border-brand-navy bg-brand-navy text-brand-orange hover:bg-brand-navy/90",
        secondary: "border-transparent bg-brand-grey-100 text-brand-grey-700 hover:bg-brand-grey-300",
        destructive: "border-transparent bg-brand-error text-white hover:bg-brand-error/80",
        outline: "text-foreground border-brand-grey-300",
        success: "border-transparent bg-brand-success text-white hover:bg-brand-success/80",
        warning: "border-transparent bg-brand-warning text-white hover:bg-brand-warning/80",
        danger: "border-transparent bg-brand-error text-white hover:bg-brand-error/80",
        dark: "border-transparent bg-brand-navy text-white hover:bg-brand-navy-mid",
        info: "border-transparent bg-brand-info text-white hover:bg-brand-info/80",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type BadgeTone = "success" | "danger" | "warning" | "info" | "neutral" | "courier" | "customer" | "admin" | "support" | "affiliate" | "partner";

const variantTone: Partial<Record<NonNullable<VariantProps<typeof badgeVariants>["variant"]>, BadgeTone>> = {
  success: "success",
  warning: "warning",
  destructive: "danger",
  danger: "danger",
  info: "info",
  dark: "info",
};

const textFromNode = (node: React.ReactNode): string => {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textFromNode).join(" ");
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return textFromNode(node.props.children);
  return "";
};

const toneFromText = (value: string): BadgeTone | undefined => {
  const key = value.toLowerCase().trim().replace(/[\s-]+/g, "_");
  if (!key || /^\d+$/.test(key)) return undefined;

  if (["courier", "driver"].includes(key)) return "courier";
  if (["customer", "client", "user"].includes(key)) return "customer";
  if (["admin", "administrator"].includes(key)) return "admin";
  if (["support", "agent", "moderator"].includes(key)) return "support";
  if (key.includes("affiliate")) return "affiliate";
  if (key.includes("partner")) return "partner";

  if (/(delivered|completed|resolved|closed|paid|published|active|approved|verified|matched|accepted|succeeded|collected|confirmed|enabled|ok|success)/.test(key)) return "success";
  if (/(cancel|failed|rejected|expired|escalated|refunded|urgent|high|overdue|breached|banned|disputed|suspended|blocked|disabled|dead_letter|error|delete)/.test(key)) return "danger";
  if (/(open|pending|waiting|progress|review|processing|unverified|medium|order_placed|ready_for_pickup|awaiting_deposit|queued|contacted)/.test(key)) return "warning";
  if (/(new|sent|normal|low|draft|in_transit|booking|custom|notification|source|template|category)/.test(key)) return "info";

  return undefined;
};

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  const dataVariant = variant ?? "default";
  const tone = toneFromText(textFromNode(props.children)) ?? variantTone[dataVariant] ?? "neutral";
  return <div data-slot="badge" data-variant={dataVariant} data-tone={tone} className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
